import { useMemo } from "react";
import { useAssessmentTabs } from "../useAssessmentTabs";
import { AssessmentTab } from "../types";

export interface TabFilterConfig {
  nearbyTab?: boolean;
}

/**
 * Hook that filters assessment tabs based on feature flags and beta access.
 *
 * This hook provides a centralized way to control which tabs and groups are
 * visible based on user permissions and feature rollout status. It handles:
 * - Beta feature visibility (site report improvements)
 * - Conditional group filtering (demographics, amenities)
 * - Empty tab removal
 *
 * Use cases:
 * - Gradual feature rollout to beta users
 * - A/B testing different tab configurations
 * - Hiding incomplete or experimental features
 * - Customizing the UI based on user permissions
 *
 * @param featureFlags - Configuration object for feature visibility
 * @returns Filtered array of assessment tabs
 */
export function useFilteredAssessmentTabs(featureFlags?: TabFilterConfig): AssessmentTab[] {
  const rawAssessmentTabs = useAssessmentTabs();

  const filteredTabs = useMemo(() => {
    if (!featureFlags) {
      return rawAssessmentTabs;
    }

    return rawAssessmentTabs.filter((tab) => tab.groups.length > 0);
  }, [rawAssessmentTabs, featureFlags]);

  return filteredTabs;
}

/**
 * Utility function that removes tabs with no groups.
 *
 * This is commonly needed after other filtering operations that might
 * leave tabs empty. Prevents rendering empty tab containers in the UI.
 *
 * @param tabs - Array of assessment tabs
 * @returns Array with empty tabs removed
 */
export function filterEmptyGroups(tabs: AssessmentTab[]): AssessmentTab[] {
  return tabs.filter((tab) => tab.groups.length > 0);
}
